import React, {useCallback, useEffect, useMemo, useRef, useState} from "react";
import {OrbitGalleryProps} from "@/components/blocks/event-rotating-image-block/types";
import {SocialIcon} from "@/components/blocks/event-rotating-image-block/so-me-icons";
import {useSwipe} from "@/components/blocks/event-rotating-image-block/useSwipe";
import {TimerBar} from "@/components/blocks/event-rotating-image-block/timer-bar";
import {usePrefersReducedMotion} from "@/components/blocks/event-rotating-image-block/usePrefersReducedMotion";
import clsx from "clsx";
import {ImageSlide} from "@/components/blocks/event-rotating-image-block/image-slide";

const clamp = (n: number, min: number, max: number) => Math.max(min, Math.min(max, n));

const TIMER_SPEED = 10000;
const ANIMATION_SPEED = 500;

export function OrbitGallery({images, socialLinks = [], translations}: OrbitGalleryProps) {
    const [index, setIndex] = useState(0);
    const [isPaused, setIsPaused] = useState(false);
    const trackRef = useRef<HTMLDivElement | null>(null);
    const containerRef = useRef<HTMLDivElement | null>(null);
    const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const leftRef = useRef<number>(-1);
    const isReducedMotion = usePrefersReducedMotion();
    const total = images?.length ?? 0;

    const enableTimer = useMemo(() => TIMER_SPEED > 0 && total > 1, [TIMER_SPEED, total]);

    const next = useCallback(() => setIndex((i) => (i + 1) % total), [total]);
    const prev = useCallback(() => setIndex((i) => (i - 1 + total) % total), [total]);
    const goTo = useCallback((i: number) => setIndex(clamp(i, 0, total - 1)), [total]);
    const pause = useCallback(() => setIsPaused(true), []);
    const resume = useCallback(() => {
        if (!enableTimer) return;
        leftRef.current = -1;
        setIsPaused(false);
    }, [enableTimer]);

    useEffect(() => {
        if (!enableTimer || isPaused || total <= 1) return;
        const startedAt = performance.now();
        timerRef.current = setTimeout(() => {
            next();
        }, TIMER_SPEED);

        return () => {
            if (timerRef.current) {
                clearTimeout(timerRef.current);
                timerRef.current = null;
                if (isPaused) {
                    const elapsed = performance.now() - startedAt;
                    leftRef.current = Math.max(0, TIMER_SPEED - elapsed);
                }
                if (!isPaused) leftRef.current = -1;
            }
        };
    }, [index, isPaused, enableTimer, total, next]);

    const onKeyDown: React.KeyboardEventHandler<HTMLDivElement> = useCallback((e) => {
        if (e.key === "ArrowRight") {
            e.preventDefault();
            next();
        }
        if (e.key === "ArrowLeft") {
            e.preventDefault();
            prev();
        }
    }, [next, prev]);

    useSwipe({ref: containerRef, onLeft: next, onRight: prev,});

    const TRANSITION_MS = isReducedMotion ? 0 : ANIMATION_SPEED;

    return (
        <div
            ref={containerRef}
            className="relative w-full select-none"
            role="region"
            aria-roledescription="carousel"
            aria-label="Image gallery"
            tabIndex={0}
            onKeyDown={onKeyDown}
            onMouseEnter={() => pause()}
            onMouseLeave={() => resume()}
        >
            <div className={clsx("relative w-full")}>
                {total > 0 && (
                    <div
                        className="absolute right-3 top-3 z-30 rounded-full bg-black/50 px-3 py-1  font-medium text-white shadow">
                        <span className="text-xs"> {index + 1} <span
                            className="opacity-80 ">{translations.pageNumber}</span> {total}  </span>
                    </div>
                )}

                {enableTimer && (
                    <TimerBar
                        key={`${index}-${isPaused}`}
                        paused={isPaused}
                        duration={TIMER_SPEED}
                        reduceMotion={isReducedMotion}
                    />
                )}

                <div className="aspect-video relative overflow-hidden">
                    <div ref={trackRef} className="will-change-transform flex" style={{
                        width: `${total * 100}%`,
                        transform: `translateX(-${(index * 100) / total}%)`,
                        transition: `transform ${TRANSITION_MS}ms linear`,
                    }}>
                        {images?.map((img, i) => (
                            <ImageSlide
                                key={i}
                                img={img}
                                i={i}
                                index={index}
                                total={total}
                                TRANSITION_MS={TRANSITION_MS}
                            />
                        ))}
                    </div>
                </div>

                {total > 1 && (
                    <div className="pointer-events-none absolute inset-0 z-50 flex items-center justify-between p-2">
                        <button
                            type="button"
                            className="size-8 pointer-events-auto inline-flex items-center justify-center rounded-full bg-black/50 p-2 text-white shadow hover:bg-black/60 focus:outline-none focus:ring-2 focus:ring-white"
                            aria-label="Previous slide"
                            onClick={() => prev()}
                        >
                            <span aria-hidden>‹</span>
                        </button>
                        <button
                            type="button"
                            className="size-8 pointer-events-auto inline-flex items-center justify-center rounded-full bg-black/50 p-2 text-white shadow hover:bg-black/60 focus:outline-none focus:ring-2 focus:ring-white"
                            aria-label="Next slide"
                            onClick={() => next()}
                        >
                            <span aria-hidden>›</span>
                        </button>
                    </div>
                )}
            </div>

            {total > 1 && (
                <div className="mt-2 flex items-center justify-center gap-2">
                    {images?.map((_, i) => (
                        <button
                            key={i}
                            type="button"
                            className={[
                                "size-2 rounded-full transition",
                                i === index ? "scale-100 bg-black" : "scale-90 bg-black/40 hover:bg-black/60",
                            ].join(" ")}
                            aria-label={`Go to slide ${i + 1}`}
                            aria-current={i === index}
                            onClick={() => goTo(i)}
                        />
                    ))}
                </div>
            )}

            {socialLinks.length > 0 && (
                <div className=" flex flex-wrap items-center gap-3 px-4 py-3">
                    {socialLinks.map((l, i) => (
                        <a key={i} href={l.url} target="_blank" className="inline-flex items-center gap-2">
                            <SocialIcon name={l.name}/>
                            <span className="text-sm">{l.name}</span>
                        </a>
                    ))}
                </div>
            )}
        </div>
    );
}

